import * as React from "react"
import { connect, Provider } from "react-redux"
import { AxiosResponse } from "axios"
import trans from "trans"
import store from "store";
import { Button, Select } from '@mus/core'

import {
    css,
    Section,
    Nav,
    NavLink,
    Icon,
    Grid,
    GridCell,
    modal,
} from "@bdfx/mergado-ui-kit"

import {
    initialFindSimilarState,
    setAvailableOptionsToStore,
    setFindSimilar,
    setIsLoadingProducts,
    setProductsToStore,
    setFIEQueriesToStore
} from "../reducer"

import {
    DiagnosticsType,
    SortByType,
    AvailableOptionsType,
    FindSimilarType,
} from "../types";

import { FiltersFormType, emptyFiltersState } from "../types/filtertypes";

import {
    updateInputValue,
    initForm
} from "app/reducers/form";

import ProductTable from "../components/product-table";
import debounce from "lodash/debounce"
import {
    eshopId,
    getAvailableOptions,
    getProducts,
    getProjectSettings,
    getSizes,
    isGodModeOn,
    isIncognitoOn,
    projectId
} from "app/helpers/api";
import SectionLoader from "app/components/section-loader";
import { iconDisabledColor } from "template-edit/components/tools-panel";

import { getIconByViewTable } from "app/helpers/other";
import { ID, ReduxList } from "app/types/app-types";
import { Project, SettingsFormType } from "app/types/model-types";
import FIEQueries from "../components/fie-queries";
import { inIframe } from "app/helpers/mergado";
import { RouterType, withRouter } from "app/components/routes";

import SaveAsQueryModalWrapper from "../components/save-as-query-modal-wrapper";
import Filters from "../components/filters";
import WatermarkGroupsViewTable from "watermark-groups/watermark-groups-viewtable";
import { allowSuperadminForUsers } from "template-edit/components/helper"
import { ObjectsType } from "products/components/watermark-marker-common"
import { selectFormValues } from "app/reducers/form"
import { createProductsExport } from "app/reducers/exports"
import { IconSimilarity } from "app/icons"
import { getHashesListFromFindSimilar } from "shape-edit/forms/helpers"
import { SizeItem } from "settings/components/sizes-settings";
import { setSizesToStore } from "template-edit/reducers";

export type ViewTableType = 'products' | 'validations' | 'watermarks' | 'watermark_groups' | 'images_quality' | 'fie_queries' | 'visual_similarity' /*| 'elements'*/
export const ViewTableTypeArr = ['products', 'validations', 'watermarks', 'watermark_groups', 'images_quality', 'fie_queries', 'visual_similarity']

export const VIEWTABLE_SESSION_NAME = 'viewTable'

interface Props {
    router: RouterType
    setSizesToStore: typeof setSizesToStore
    sizes: ReduxList<SizeItem>
    createProductsExport: typeof createProductsExport
    initForm: typeof initForm
    setFindSimilar: typeof setFindSimilar
    filtersForm: FiltersFormType
    settingsForm: SettingsFormType
    setProductsToStore: typeof setProductsToStore
    setIsLoadingProducts: typeof setIsLoadingProducts
    updateInputValue: typeof updateInputValue
    setAvailableOptionsToStore: typeof setAvailableOptionsToStore,
    setFIEQueriesToStore: typeof setFIEQueriesToStore
    products: ReduxList<DiagnosticsType>
    project: Project
    findSimilar: FindSimilarType
}

interface State {
    isLoading: boolean
    viewTable: ViewTableType
    isDirty: boolean
    selectedIds: number[]
    verticalMode: boolean
}


class Products extends React.Component<Props, State> {

    constructor(props: Props) {
        super(props)

        const viewTable = props.router.params.viewtable ? props.router.params.viewtable : sessionStorage.getItem(VIEWTABLE_SESSION_NAME) as ViewTableType
        if (props.router.params.viewtable && ViewTableTypeArr.includes(props.router.params.viewtable)) {
            sessionStorage.setItem(VIEWTABLE_SESSION_NAME, props.router.params.viewtable)
        }

        this.state = {
            isLoading: false,
            viewTable: ViewTableTypeArr.includes(viewTable) ? viewTable : 'products',   //default view table
            isDirty: false,
            selectedIds: null,
            verticalMode: false,
        }

        if (Object.keys(props.filtersForm).length === 0) {
            props.initForm("filtersForm", this.getEmptyFiltersForm())
        }
    }

    componentDidMount(): void {
        getAvailableOptions()
            .then((response: AxiosResponse<AvailableOptionsType>) => {
                this.props.setAvailableOptionsToStore(response.data)
            })
            .catch((error) => {
                console.error('cannot get available options', error)
            })

        this.setState({ isLoading: true })
        getProjectSettings()
            .then((response: AxiosResponse<SettingsFormType>) => {
                const settings: SettingsFormType = response.data
                this.props.initForm('settingsForm', settings)

                const updatedFiltersForm = {
                    ...this.props.filtersForm,
                    productsPerPage: settings.products_per_page ?? 10,
                    groupByImage: !!(settings.group_by_image ?? false),
                    paginator: 1
                }

                this.props.initForm('filtersForm', updatedFiltersForm)
                this.updateProducts(updatedFiltersForm)
            })
            .catch(error => {
                console.error('cannot get project settings', error)
                this.updateBounced()
            })
            .finally(() => {
                this.setState({ isLoading: false })
            })

        getSizes()
            .then((response: AxiosResponse<SizeItem[]>) => {
                this.props.setSizesToStore(response.data)
            })
            .catch((e) => console.error('cannot get sizes', e))
    }

    updateWatermarkMasksFromDb = (objects: ObjectsType) => {
        const onlyProductIds = this.props.products.data?.products?.data.map(product => product.product_id) //refresh results for existing ids: where products.id in ( [ten products] ) without filters changed!
        this.updateProducts(this.props.filtersForm, onlyProductIds, true, getHashesListFromFindSimilar(this.props.findSimilar))
    }

    setIsDirty = (value: boolean) => {
        this.setState({ isDirty: value })
    }

    getEmptyFiltersForm = (): FiltersFormType => {
        const { filtersForm } = this.props
        return {
            ...emptyFiltersState,
            productsPerPage: filtersForm.productsPerPage,
            groupByImage: filtersForm.groupByImage
        }
    }

    setViewTable = (viewTable: ViewTableType) => {
        sessionStorage.setItem(VIEWTABLE_SESSION_NAME, viewTable);
        this.setState({ viewTable: viewTable })
    }

    switchFiltersMode = () => {
        const { verticalMode } = this.state
        this.setState({ verticalMode: !verticalMode })
    }

    updateProducts = (filters: FiltersFormType, onlyProductIds: ID[] = [], downloadImagesIfNotDownloaded = false, onlyImageHashes: string[] = null) => {
        this.props.setIsLoadingProducts(true)
        getProducts(filters, onlyProductIds, downloadImagesIfNotDownloaded, onlyImageHashes)
            .then((response: AxiosResponse<DiagnosticsType>) => {
                this.props.setProductsToStore(response.data)
            })
            .catch((e) => console.error('cannot get products'))
            .finally(() => this.props.setIsLoadingProducts(false))
    }

    updateBounced = debounce(() => {
        this.updateProducts(this.props.filtersForm, [], true, getHashesListFromFindSimilar(this.props.findSimilar))
    }, 2200)


    updateBouncedFast = debounce(() => {
        this.updateProducts(this.props.filtersForm, [], true, getHashesListFromFindSimilar(this.props.findSimilar))
    }, 800)


    updateProductsBounced = () => {
        this.setState({ isLoading: true })
        this.props.updateInputValue('filtersForm', 'paginator', 1)
        this.updateBounced()
    }


    handlePaginator = (page: number) => {
        if (page) {
            this.props.updateInputValue('filtersForm', 'paginator', page)
        } else {
            this.props.updateInputValue('filtersForm', 'paginator', 1)
        }

        this.updateBouncedFast()
    }

    handleSortBy = (sortBy: SortByType) => {
        if (sortBy) {
            this.props.updateInputValue('filtersForm', 'sortBy', sortBy)
        } else {
            this.props.updateInputValue('filtersForm', 'sortBy', null)
        }

        this.updateBouncedFast()
    }

    changeViewTable = (newViewTable: ViewTableType) => {
        const productsUrl = `/eshop/${eshopId}/project/${projectId}/products/${newViewTable}`
        this.props.updateInputValue('filtersForm', 'paginator', 1)
        this.setViewTable(newViewTable)
        /*history.pushState(this.state, trans(newViewTable), productsUrl)*/
        this.props.router.navigate(productsUrl)
    }

    getIconByViewTable = (viewTable: string, iconSize: number = 32, iconColor: string = iconDisabledColor) => {
        if (viewTable === 'visual_similarity') {
            return <IconSimilarity size={iconSize} text={trans(viewTable)} color={iconColor} />
        }
        return <Icon type={getIconByViewTable(viewTable)} size={iconSize} text={trans(viewTable)} color={iconColor} />
    }

    resetFiltersButton = () => {
        this.props.initForm('filtersForm', this.getEmptyFiltersForm())
        this.updateBouncedFast()
        this.setIsDirty(false)
        this.props.setFindSimilar({...initialFindSimilarState})
    }

    filterNow = () => {
        this.props.updateInputValue('filtersForm', 'paginator', 1)
        this.updateBouncedFast()  //wait for paginator to change before we send the request to api
        this.setIsDirty(false)
    }

    saveAsQuery = () => {
        if (!this.props.products.isLoading) {
            this.filterNow()
            modal({
                overlayClassName: 'save-as-query-modal',
                title: trans("save_as_query_title"),
                closeOnEscape: false,
                closeOnClickOutside: false,
                message: (close) => <Provider store={store}>
                    <SaveAsQueryModalWrapper
                        filtersForm={this.props.filtersForm}
                        hideModal={close} viewTable={this.state.viewTable}
                        setFIEQueriesToStore={this.props.setFIEQueriesToStore}
                    />
                </Provider>
            })
        }
    }

    render() {
        const { filtersForm, project, products } = this.props
        const { viewTable, isLoading, verticalMode } = this.state
        const iconSize = 14
        const userId = project.user && project.user.id ? project.user.id : null

        if (!filtersForm || !filtersForm.productsPerPage || !products.isLoaded) {
            return <SectionLoader />
        }

        const navheader = <StyledNav title={'show'}>
            <NavLink active={viewTable === 'products'} link={
                <a onClick={() => this.changeViewTable('products')}>
                    {this.getIconByViewTable('products', iconSize)}
                </a>
            } />
            <NavLink active={viewTable === 'validations'} link={
                <a onClick={() => this.changeViewTable('validations')}>
                    {this.getIconByViewTable('validations', iconSize)}
                </a>
            } />
            <NavLink active={viewTable === 'watermarks'} link={
                <a onClick={() => this.changeViewTable('watermarks')}>
                    {this.getIconByViewTable('watermarks', iconSize)}
                </a>
            } />
            {userId && (allowSuperadminForUsers.includes(userId) || (!isIncognitoOn && isGodModeOn && this.props.project.godAllProjects)) &&
                <NavLink active={viewTable === 'watermark_groups'} className={'some-users-only'} link={
                    <a onClick={() => this.changeViewTable('watermark_groups')}>
                        {this.getIconByViewTable('watermark_groups', iconSize)}
                    </a>
                } />
            }
            <NavLink active={viewTable === 'images_quality'} link={
                <a onClick={() => this.changeViewTable('images_quality')}>
                    {this.getIconByViewTable('images_quality', iconSize)}
                </a>
            } />

            <NavLink active={viewTable === 'visual_similarity'} link={
                <a onClick={() => this.changeViewTable('visual_similarity')}>
                    {this.getIconByViewTable('visual_similarity', iconSize)}
                </a>
            } />

            <NavLink active={viewTable === 'fie_queries'} link={
                <a onClick={() => this.changeViewTable('fie_queries')}>
                    {this.getIconByViewTable('fie_queries', iconSize)}
                </a>
            } />
        </StyledNav>

        return (
            <StyledMain>
                <StyledSection
                    /*prefix={`${viewTable}`}*/
                    header={navheader}
                    className={`section-${viewTable} ${isLoading ? 'muk-helper-loading-line with-line' : ''}`}>

                    {viewTable === 'watermark_groups' &&
                        <WatermarkGroupsViewTable />
                    }

                    {viewTable === 'fie_queries' &&
                        <FIEQueries setViewTable={this.setViewTable} filterNow={this.filterNow} />
                    }

                    {['products', 'images', 'validations', 'watermarks', 'images_quality', 'visual_similarity'].includes(viewTable) &&
                        <StyledGrid dataVerticalMode={verticalMode}>
                            <GridCell id={'filters'}>
                                {filtersForm &&
                                    <Filters
                                        updateProducts={this.updateProducts}
                                        updateProductsBounced={this.updateProductsBounced}
                                        updateProductsBouncedFast={this.updateBouncedFast}
                                        getEmptyFiltersForm={this.getEmptyFiltersForm}
                                        setIsDirty={this.setIsDirty}
                                        isDirty={this.state.isDirty}
                                        userId={userId}
                                        verticalMode={verticalMode}
                                    />
                                }
                            </GridCell>
                            <GridCell style={{ overflow: 'auto' }}>
                                {['products', 'images', 'validations', 'watermarks', 'images_quality', 'visual_similarity'].includes(viewTable) &&
                                    <ProductTable
                                        saveAsQuery={this.saveAsQuery}
                                        resetFiltersButton={this.resetFiltersButton}
                                        filterNow={this.filterNow}
                                        handlePaginator={this.handlePaginator}
                                        handleSortBy={this.handleSortBy}
                                        setViewTable={this.setViewTable}
                                        viewTable={viewTable}
                                        switchFiltersMode={this.switchFiltersMode}
                                        filtersMode={this.state.verticalMode}
                                        getEmptyFiltersForm={this.getEmptyFiltersForm}
                                        updateProductsBouncedFast={this.updateBouncedFast}
                                        isDirty={this.state.isDirty}
                                        updateWatermarkMasksFromDb={this.updateWatermarkMasksFromDb}
                                    />
                                }
                            </GridCell>
                        </StyledGrid>
                    }
                </StyledSection>
            </StyledMain>
        )
    }
}

const StyledGrid = css(Grid)({
    gridTemplateColumns: '250px auto',
    gridGap: '10px',
}, props => {
    return {
        gridTemplateColumns: props.dataVerticalMode ? '250px auto' : 'auto',
        '> .muk-gridcell': {
            overflowX: props.dataVerticalMode ? 'hidden' : undefined,
            overflowY: props.dataVerticalMode ? 'auto' : undefined,
            maxHeight: props.dataVerticalMode ? "calc(100vh - 180px)" : undefined,
            '@media screen and (max-width: 1024px)': {
                maxHeight: undefined,
            },
        },
        '@media screen and (max-width: 1024px)': {
            gridTemplateColumns: 'auto',
        },
    }
})

const StyledSection = css(Section)({
    ' .nowrap': {
        whiteSpace: 'nowrap',
    },
    ' > .m-suffix': {
        '@media screen and (max-width: 1024px)': {
            visibility: "hidden",
            display: "none",
        },
    },
    ' > .m-content': {
        padding: '10px',
    },
    ' .m-prefix .muk-button': {
        padding: '5px 10px',
        alignSelf: 'center',
    },
    ' .m-header-content': {
        padding: '16px 0px !important',
    },
    ' .muk-nav': {
        marginTop: '-14px !important',
    },
    '@media screen and (max-width: 1024px)': {
        ' .m-content': {
            paddingBottom: inIframe() ? '100px' : '10px',
            overflow: 'auto',
        },
        ' .m-header': {
            gridTemplateColumns: 'auto',
        },
        ' .m-header-content': {
            overflowX: 'auto',
            padding: '0 !important',
        },
        ' .muk-nav': {
            marginTop: '0 !important',
        },
    }
})

const StyledNav = css(Nav)({
    padding: 0,
    margin: 0,
    float: 'right',
    ' .muk-navlink a': {
        fontWeight: "normal",
    },
    ' .muk-navlink .muk-icon .m-image': {
        marginRight: '4px',
    },
    '@media screen and (max-width: 1024px)': {
        float: undefined,
        ' ul.m-links': {
            display: 'flex',
            width: '100vw',
        },
    },
})

const StyledMain = css('div')({
    ' .reload-needed': {
        position: 'relative',
        overflow: 'hidden',
        "&:after": {
            position: 'absolute',
            pointerEvents: 'none',
            top: 0,
            left: '-25px',
            content: '" "',
            width: 'calc(100% + 50px)',
            height: '100%',
            opacity: '0.5',
            background: 'repeating-linear-gradient(-55deg,#ccc,#ccc 10px,#dadada 20px,#dadada 20px)',
            backgroundSize: '25px 100%',
            zIndex: '1000000',
        },
    },
    ' .section-watermark_groups': {
        minHeight: 'calc(100vh - 110px)',
    },
})

function mapStateToProps(state) {
    return {
        project: state.project.data,
        sizes: state.sizes,
        filtersForm: selectFormValues(state, 'filtersForm'),
        settingsForm: selectFormValues(state, 'settingsForm'),
        findSimilar: state.findSimilar,
        products: state.products,
    }
}

export default connect(mapStateToProps, {
    initForm,
    updateInputValue,
    createProductsExport,
    setAvailableOptionsToStore,
    setFIEQueriesToStore,
    setFindSimilar,
    setProductsToStore,
    setIsLoadingProducts,
    setSizesToStore,
})(withRouter(Products))